import React from 'react';
import { Button } from '@parsina/framework';
import { C, GRADIENT_BRAND } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { displayName } from '../../lib/appointments.js';
import StatusPill from '../StatusPill.jsx';
import PatientAvatar from '../PatientAvatar.jsx';
import NextServiceChip from '../NextServiceChip.jsx';

// The «نفر بعدی» hero (§4 ④).
//
// On the desktop this is an `Alert variant="info"` in the page flow. On a phone
// the single most important fact of the screen — who walks in next — gets the
// brand gradient and the top of the fold, because a glance is the whole
// interaction: §1's mobile job list opens with "glance at the day".
//
// `apt` comes from `buildDayModel().nextApt`, which is null off today and null
// with nobody waiting. This component never decides who is next — nor what they
// are coming FOR: `service` is read beside it (hooks/useNextService.js), and
// null renders as «ویزیت».

const MobileNextPatient = ({ apt, service, onOpen, onOpenVisit }) => {
  const { t, formatDigits } = useTranslation();
  if (!apt) return null;

  const name = displayName(apt) || t('today.row.unknownPatient');

  return (
    <section
      data-testid="today-next"
      style={{
        background: GRADIENT_BRAND,
        borderRadius: '1rem',
        padding: '0.875rem',
        color: '#fff',
        boxShadow: '0 6px 20px rgba(0,0,0,0.14)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        <PatientAvatar name={name} src={apt.patient?.avatarUrl} size={40} bg="rgba(255,255,255,0.22)" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.75)' }}>
            {t('today.next.title')}
          </div>
          <div
            dir="auto"
            style={{
              fontSize: '0.9375rem',
              fontWeight: 800,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </div>
          {apt.time && (
            <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.8)' }}>
              {t('today.next.at', { time: formatDigits(apt.time) })}
            </div>
          )}
          {/* The treatment shares the hero's narrow column and ellipses inside
              it; the «+۲» never does, because a truncated count is a lie. */}
          {apt.patientId && <NextServiceChip service={service} compact />}
        </div>
        <StatusPill status={apt.status} />
      </div>

      {/* Only when there IS a record to open — a walk-in row carries a label
          instead of a patientId, and a control that navigates nowhere is a
          defect (§3.2).

          TWO buttons, the desktop hero's pair in the desktop hero's order:
          جلسه is the primary action of the day sheet, so the hero that shouts
          "this patient is next" has to be the way INTO the visit. It was a
          single «پرونده» button that navigated to the visit workspace anyway —
          the label and the destination disagreed, and the one action the
          dentist opens the app for had no button of its own. Stacked rather
          than laid side by side: at 320px two buttons on one line truncate
          each other's label, and both need the 44px touch target. */}
      {apt.patientId && (
        <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
          <Button
            onClick={() => onOpenVisit?.(apt)}
            data-testid="today-next-visit"
            aria-label={t('today.row.visitFor', { name })}
            className="w-full min-h-11 hover:bg-white/90"
            style={{ background: '#fff', color: C.navy, fontWeight: 800 }}
          >
            {t('today.row.visit')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpen?.(apt)}
            data-testid="today-next-open"
            aria-label={t('today.row.openFor', { name })}
            className="w-full min-h-11 text-white hover:bg-white/25"
            style={{ background: 'rgba(255,255,255,0.18)', color: '#fff' }}
          >
            {t('today.row.open')}
          </Button>
        </div>
      )}
    </section>
  );
};

export default MobileNextPatient;
